import {
  Box,
  Switch,
  Select,
  MenuItem,
  TableRow,
  TableCell,
  TextField,
  Typography,
  FormControl,
} from '@mui/material';
import { useState, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';
import { ProductType } from 'src/type/product';
import { UserLevelType } from 'src/type/user';
import { UserLevelProduct } from 'src/type/user-level-product';
import { PriceSource, UserLevelPrice } from 'src/type/user-level-price';
import { ProductPriceLog } from 'src/type/product-price-log';
import { RealtimeLevelPrice } from 'src/type/realtime-level-price';
import { toastError } from 'src/utils/format-error';
import { enqueueSnackbar } from 'notistack';

import { getRealtimeNetPrice } from './realtime-net-price';

// ----------------------------------------------------------------------

type Props = {
  level: UserLevelType;
  products: ProductType[];
  userLevelProducts: UserLevelProduct[];
  userLevelPrices: UserLevelPrice[];
  productPriceLogs: ProductPriceLog[];
  /** This level's live prices (productId → price) from the Realtime Database. */
  realtimePrices?: Record<string, RealtimeLevelPrice | null>;
  onUserLevelProductsUpdate: (updatedUserLevelProducts: UserLevelProduct[]) => void;
  onUserLevelPricesUpdate: (updatedUserLevelPrices: UserLevelPrice[]) => void;
  onUserLevelUpdate?: (updatedUserLevel: UserLevelType) => void;
};

export default function SettingSourcePriceTableRow({
  level,
  products,
  userLevelProducts,
  userLevelPrices,
  productPriceLogs,
  realtimePrices,
  onUserLevelProductsUpdate,
  onUserLevelPricesUpdate,
  onUserLevelUpdate,
}: Props) {
  const sortedProducts = products.sort((a, b) => a.sequence - b.sequence);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(level.description);

  const handleDescriptionUpdate = useCallback(async () => {
    if (!onUserLevelUpdate || descriptionValue === level.description) {
      setIsEditingDescription(false);
      return;
    }

    setIsUpdating(true);
    try {
      await axios.patch(endpoints.userLevel.update(level.id), {
        description: descriptionValue,
      });

      const updatedLevel: UserLevelType = {
        ...level,
        description: descriptionValue,
        updatedAt: new Date().toISOString(),
      };

      onUserLevelUpdate(updatedLevel);
      setIsEditingDescription(false);
      enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
    } catch (error) {
      console.error('Error updating user level description:', error);
      toastError(error);
      setDescriptionValue(level.description); // Reset to original value
    } finally {
      setIsUpdating(false);
    }
  }, [descriptionValue, level, onUserLevelUpdate]);

  const handleDescriptionCancel = useCallback(() => {
    setDescriptionValue(level.description);
    setIsEditingDescription(false);
  }, [level.description]);

  const isProductEnabled = useCallback(
    (userLevelId: string, productId: string): boolean =>
      userLevelProducts
        .find((ulp: UserLevelProduct) => ulp.userLevelId === userLevelId)
        ?.productIds.includes(productId) || false,
    [userLevelProducts]
  );

  const findUserLevelPrice = useCallback(
    (userLevelId: string, productId: string): UserLevelPrice | undefined =>
      userLevelPrices.find(
        (ulp: UserLevelPrice) => ulp.userLevelId === userLevelId && ulp.productId === productId
      ),
    [userLevelPrices]
  );

  const handleToggleProduct = useCallback(
    async (userLevelId: string, productId: string) => {
      if (isUpdating) return; // Prevent multiple calls

      // Check if product is enabled
      const product = products.find((p) => p.id === productId);
      if (!product?.isEnable) return; // Prevent toggle if product is disabled

      const userLevelProduct = userLevelProducts.find(
        (ulp: UserLevelProduct) => ulp.userLevelId === userLevelId
      );
      if (!userLevelProduct) return;

      setIsUpdating(true);

      const isEnabled = userLevelProduct.productIds.includes(productId);
      const updatedProductIds = isEnabled
        ? userLevelProduct.productIds.filter((id: string) => id !== productId)
        : [...userLevelProduct.productIds, productId];

      const updatedUserLevelProduct: UserLevelProduct = {
        ...userLevelProduct,
        productIds: updatedProductIds,
        updatedAt: new Date().toISOString(),
      };

      try {
        // Update local state immediately for better UX
        const updatedUserLevelProducts = userLevelProducts.map((ulp) =>
          ulp.id === userLevelProduct.id ? updatedUserLevelProduct : ulp
        );
        onUserLevelProductsUpdate(updatedUserLevelProducts);

        // Update via API with debounce
        await new Promise((resolve) => setTimeout(resolve, 300)); // 300ms debounce
        await axios.patch(endpoints.userLevelProduct.update(userLevelProduct.id), {
          productIds: updatedProductIds,
        });

        enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      } catch (error) {
        console.error('Error updating user level product:', error);
        toastError(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [userLevelProducts, onUserLevelProductsUpdate, isUpdating, products]
  );

  const handlePriceSourceChange = useCallback(
    async (userLevelId: string, productId: string, type: 'buy' | 'sell', value: PriceSource) => {
      if (isUpdating) return; // Prevent multiple calls

      // Check if product is enabled
      const product = products.find((p) => p.id === productId);
      if (!product?.isEnable) return; // Prevent change if product is disabled

      const existingPrice = userLevelPrices.find(
        (ulp: UserLevelPrice) => ulp.userLevelId === userLevelId && ulp.productId === productId
      );

      if (!existingPrice) {
        toastError('Price data not found');
        return;
      }

      setIsUpdating(true);

      const updatedPrice: Partial<UserLevelPrice> = {
        ...existingPrice,
        [type === 'buy' ? 'buySourcePrice' : 'sellSourcePrice']: value,
      };

      try {
        // Update local state immediately for better UX
        const updatedUserLevelPrices = userLevelPrices.map((ulp) =>
          ulp.id === existingPrice.id ? updatedPrice : ulp
        );
        onUserLevelPricesUpdate(updatedUserLevelPrices as UserLevelPrice[]);

        // Update via API with debounce
        await new Promise((resolve) => setTimeout(resolve, 500)); // 500ms debounce for price changes
        await axios.patch(endpoints.userLevelPrice.update(existingPrice.id), {
          buySourcePrice: updatedPrice.buySourcePrice,
          sellSourcePrice: updatedPrice.sellSourcePrice,
        });

        enqueueSnackbar('อัพเดทข้อมูลสำเร็จ', { variant: 'success' });
      } catch (error) {
        console.error('Error updating user level price:', error);
        toastError(error);
      } finally {
        setIsUpdating(false);
      }
    },
    [userLevelPrices, onUserLevelPricesUpdate, isUpdating, products]
  );

  const getPriceSourceOptions = (productId: string, type: 'buy' | 'sell') => {
    const priceLog = productPriceLogs.find((log) => log.productId === productId);
    const product = products.find((p) => p.id === productId);
    const isSilverProduct = product && /เงิน|silver/i.test(product.name);
    const options = [];

    if (priceLog) {
      const getAssociationPrice = () => {
        if (type === 'buy') {
          return isSilverProduct ? priceLog.priceBuyPerGram : priceLog.priceBuyPerUnit;
        }
        return isSilverProduct ? priceLog.priceSellPerGram : priceLog.priceSellPerUnit;
      };

      const getHshPrice = () => {
        if (type === 'buy') {
          return isSilverProduct ? priceLog.hshPriceBuyPerGram : priceLog.hshPriceBuyPerUnit;
        }
        return isSilverProduct ? priceLog.hshPriceSellPerGram : priceLog.hshPriceSellPerUnit;
      };

      const getCustomPrice = () => {
        if (type === 'buy') {
          return isSilverProduct ? priceLog.customPriceBuyPerGram : priceLog.customPriceBuyPerUnit;
        }
        return isSilverProduct ? priceLog.customPriceSellPerGram : priceLog.customPriceSellPerUnit;
      };

      // Check ASSOCIATION prices
      const associationPrice = getAssociationPrice();
      if (associationPrice && associationPrice > 0) {
        options.push({ value: PriceSource.ASSOCIATION, label: PriceSource.ASSOCIATION });
      }

      // Check Online prices
      const hshPrice = getHshPrice();
      if (hshPrice && hshPrice > 0) {
        options.push({ value: PriceSource.HSH, label: PriceSource.HSH });
      }

      // Check CUSTOM prices
      const customPrice = getCustomPrice();
      if (customPrice && customPrice > 0) {
        options.push({ value: PriceSource.CUSTOM, label: PriceSource.CUSTOM });
      }
    }

    // If no valid prices found, show at least one option to prevent empty select
    if (options.length === 0) {
      options.push({ value: PriceSource.HSH, label: PriceSource.HSH });
    }

    return options;
  };

  const hasValidPrices = (productId: string) => {
    const priceLog = productPriceLogs.find((log) => log.productId === productId);
    const product = products.find((p) => p.id === productId);
    const isSilverProduct = product && /เงิน|silver/i.test(product.name);
    if (!priceLog) return false;

    const isPriceValid = (price: number) => price && price > 0;

    // Check if there's at least one valid price for either buy or sell
    const hasValidBuyPrice =
      (isSilverProduct && isPriceValid(priceLog.priceBuyPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.priceBuyPerUnit)) ||
      (isSilverProduct && isPriceValid(priceLog.hshPriceBuyPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.hshPriceBuyPerUnit)) ||
      (isSilverProduct && isPriceValid(priceLog.customPriceBuyPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.customPriceBuyPerUnit));

    const hasValidSellPrice =
      (isSilverProduct && isPriceValid(priceLog.priceSellPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.priceSellPerUnit)) ||
      (isSilverProduct && isPriceValid(priceLog.hshPriceSellPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.hshPriceSellPerUnit)) ||
      (isSilverProduct && isPriceValid(priceLog.customPriceSellPerGram)) ||
      (!isSilverProduct && isPriceValid(priceLog.customPriceSellPerUnit));

    return hasValidBuyPrice || hasValidSellPrice;
  };

  const renderPriceSelect = (
    product: ProductType,
    userLevelId: string,
    price: UserLevelPrice | undefined,
    type: 'buy' | 'sell'
  ) => {
    // Only show "ปิด" text when user level product is disabled, not when product.isEnable is false
    if (!isProductEnabled(userLevelId, product.id)) {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // minHeight: 32,
            height: 1,
          }}
        >
          <Typography variant="body2" color="error">
            ปิด
          </Typography>
        </Box>
      );
    }

    const currentValue = type === 'buy' ? price?.buySourcePrice : price?.sellSourcePrice;
    const options = getPriceSourceOptions(product.id, type);

    // Use the actual value from API, or fallback to HSH if no value exists
    const selectValue = currentValue || PriceSource.HSH;

    // Check if this is a valid enum value
    const isValidValue = Object.values(PriceSource).includes(selectValue as PriceSource);
    const finalValue = isValidValue ? selectValue : PriceSource.HSH;

    // Get the current price based on selected source
    const priceLog = productPriceLogs.find((log) => log.productId === product.id);
    const isSilverProduct = /เงิน|silver/i.test(product.name);
    let currentPrice = 0;

    if (priceLog) {
      const getPriceBySource = () => {
        if (type === 'buy') {
          if (finalValue === PriceSource.ASSOCIATION) {
            return isSilverProduct
              ? (priceLog.priceBuyPerGram || 0) * 1000
              : priceLog.priceBuyPerUnit;
          }
          if (finalValue === PriceSource.HSH) {
            return isSilverProduct
              ? (priceLog.hshPriceBuyPerGram || 0) * 1000
              : priceLog.hshPriceBuyPerUnit;
          }
          if (finalValue === PriceSource.CUSTOM) {
            return isSilverProduct
              ? (priceLog.customPriceBuyPerGram || 0) * 1000
              : priceLog.customPriceBuyPerUnit;
          }
        } else {
          if (finalValue === PriceSource.ASSOCIATION) {
            return isSilverProduct
              ? (priceLog.priceSellPerGram || 0) * 1000
              : priceLog.priceSellPerUnit;
          }
          if (finalValue === PriceSource.HSH) {
            return isSilverProduct
              ? (priceLog.hshPriceSellPerGram || 0) * 1000
              : priceLog.hshPriceSellPerUnit;
          }
          if (finalValue === PriceSource.CUSTOM) {
            return isSilverProduct
              ? (priceLog.customPriceSellPerGram || 0) * 1000
              : priceLog.customPriceSellPerUnit;
          }
        }
        return 0;
      };

      currentPrice = getPriceBySource();
    }

    // Customer-facing price for this level, identical to what the mobile app shows.
    const netPrice = getRealtimeNetPrice(realtimePrices?.[product.id], type, isSilverProduct);
    // Values are already cut to 2 decimals like the app; gold hides a zero fraction.
    const formatAmount = (value: number) =>
      value.toLocaleString('th-TH', {
        minimumFractionDigits: isSilverProduct ? 2 : 0,
        maximumFractionDigits: 2,
      });
    const unitSuffix = isSilverProduct ? ' บาท/กก.' : ' บาท';
    const formatProfit = (profit: number) => {
      if (!profit) return '';
      const sign = profit > 0 ? '+' : '−';
      return ` (${sign}${Math.abs(profit).toLocaleString('th-TH', { maximumFractionDigits: 3 })})`;
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <FormControl size="small" fullWidth>
          <Select
            value={finalValue}
            disabled={isUpdating || !product.isEnable}
            onChange={(event) =>
              handlePriceSourceChange(
                userLevelId,
                product.id,
                type,
                event.target.value as PriceSource
              )
            }
            displayEmpty
            variant="outlined"
            sx={{
              minHeight: 32,
              backgroundColor: 'transparent',
              '& .MuiSelect-select': {
                py: 0.5,
                px: 1,
                fontSize: '0.875rem',
                backgroundColor: 'transparent',
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textAlign: 'center',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Typography
                  variant="body2"
                  sx={{
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    maxWidth: '100px',
                    mx: 'auto',
                  }}
                >
                  {option.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Net price (same as the app) with the source price + level profit below it */}
        {netPrice ? (
          <>
            <Typography
              variant="body2"
              sx={{ mt: 0.5, fontWeight: 'fontWeightBold', color: 'text.primary' }}
            >
              {formatAmount(netPrice.net)}
              {unitSuffix}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
              ต้นทาง {formatAmount(netPrice.source)}
              {formatProfit(netPrice.profit)}
            </Typography>
          </>
        ) : (
          currentPrice > 0 && (
            <>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, fontWeight: 'fontWeightBold', color: 'text.disabled' }}
              >
                -
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                ต้นทาง {formatAmount(currentPrice)}
              </Typography>
            </>
          )
        )}
      </Box>
    );
  };

  return (
    <TableRow key={level.id}>
      <TableCell
        align="center"
        sx={{
          fontWeight: 'fontWeightMedium',
        }}
      >
        {level.name}
      </TableCell>
      <TableCell align="center">
        {isEditingDescription ? (
          <TextField
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            onBlur={handleDescriptionUpdate}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleDescriptionUpdate();
              } else if (e.key === 'Escape') {
                handleDescriptionCancel();
              }
            }}
            size="small"
            variant="outlined"
            disabled={isUpdating}
            autoFocus
            sx={{
              minWidth: 120,
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem',
              },
            }}
          />
        ) : (
          <Typography
            variant="body2"
            onClick={() => setIsEditingDescription(true)}
            sx={{
              cursor: 'pointer',
              minHeight: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderRadius: 1,
              },
              px: 1,
              py: 0.5,
            }}
          >
            {level.description}
          </Typography>
        )}
      </TableCell>
      <TableCell
        sx={{
          p: 0,
        }}
      >
        <Box sx={{ display: 'flex' }}>
          {sortedProducts.map((product) => {
            const productPrice = findUserLevelPrice(level.id, product.id);

            return (
              <Box
                key={product.id}
                sx={{
                  minWidth: 320,
                  flex: 1,
                }}
              >
                <Box sx={{ display: 'flex', height: 1 }}>
                  <Box
                    sx={{
                      flex: 0.6,
                      p: 1,
                      textAlign: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Switch
                      checked={isProductEnabled(level.id, product.id)}
                      size="small"
                      disabled={isUpdating || !product.isEnable || !hasValidPrices(product.id)}
                      onChange={() => handleToggleProduct(level.id, product.id)}
                    />
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {renderPriceSelect(product, level.id, productPrice, 'buy')}
                  </Box>
                  <Box
                    sx={{
                      flex: 1,
                      p: 0.5,
                      textAlign: 'center',
                    }}
                  >
                    {renderPriceSelect(product, level.id, productPrice, 'sell')}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </TableCell>
    </TableRow>
  );
}
